/**
 * web/src/lib/ws/handleBroadcast.ts
 * Dispatches a BroadcastEvent to the TanStack Query cache.
 *
 * D-2.1 hybrid strategy (all keys via the qk factory — see lib/queryKeys.ts):
 *   1. Apply an IN-PLACE patch across every qk.workflows.list(...) entry for
 *      any workflow-affecting event so list cards transition status without a
 *      refetch flicker. `setQueriesData` on the broad qk.workflows.root()
 *      prefix reaches all filter variants (an earlier `setQueryData(['workflows'])`
 *      wrote the bare key no list query reads — F12-class drift, now fixed).
 *      Only known-good fields are rewritten: status, currentStepIndex,
 *      completedAt, failureReason.
 *   2. THEN narrow-invalidate qk.workflows.root() with refetchType: 'active'
 *      as a safety net — only the actively-mounted list refetches.
 *   3. Always invalidate qk.workflow.root(workflowId) (the open detail page)
 *      because Detail needs the full step + audit + tracked-email join.
 *   4. Email events invalidate the broad qk.trackedEmails.root() prefix so the
 *      Confirmations list reconciles regardless of its active status filter
 *      (the old ['tracked-emails','pending'] target was read by no mounted
 *      component — F12).
 *
 * Every invalidation goes through invalidateFresh() (2026-07-21): React Query
 * SWALLOWS an invalidation that lands while a query's FIRST fetch is in flight
 * (query.fetch dedupes onto the in-flight promise when state.data is still
 * undefined — cancelRefetch only applies once data exists). The speaker
 * journey's first load reads monday live and takes seconds, so a gate_opened
 * arriving in that window used to vanish: the pre-commit response landed,
 * cleared the invalidated flag, and the page sat on "In progress" until a
 * manual refresh. invalidateFresh cancels such first-load fetches before
 * invalidating so the refetch always reads post-event state.
 */
import type { InvalidateQueryFilters, QueryClient } from '@tanstack/react-query';
import type { BroadcastEvent } from './types';
import type { Workflow } from '../../api/types';
import { qk } from '../queryKeys';

/**
 * Invalidate `filters.queryKey`, guaranteeing the refetch reads post-event
 * state. Plain invalidateQueries when nothing under the key is mid-first-load;
 * otherwise cancel the in-flight fetches first (their responses were read from
 * the DB BEFORE this broadcast's commit) and invalidate once the cancel lands.
 *
 * @param qc the global QueryClient
 * @param filters invalidation filters (queryKey + optional refetchType)
 * @returns void — fire-and-forget; RQ retries/errors surface via the query
 * @sideEffects may cancel in-flight fetches under the key
 * @idempotent yes — converges to one fresh fetch per active query
 */
export function invalidateFresh(qc: QueryClient, filters: InvalidateQueryFilters): void {
  const firstLoadInFlight = qc
    .getQueryCache()
    .findAll({ queryKey: filters.queryKey, fetchStatus: 'fetching' })
    .some((q) => q.state.data === undefined);
  if (!firstLoadInFlight) {
    void qc.invalidateQueries(filters);
    return;
  }
  void qc.cancelQueries({ queryKey: filters.queryKey }).then(() => qc.invalidateQueries(filters));
}

/**
 * In-place patch helper. Maps over the cached `['workflows']` array and
 * rewrites the matched workflow's known-good fields from the WS payload.
 * Returns a new array reference (immutable update — required for React
 * Query subscribers to re-render). Returns `old` unchanged when there's
 * nothing to patch.
 *
 * @param old current cached list (or undefined when the query hasn't loaded)
 * @param ev incoming BroadcastEvent
 * @returns new array with the matched workflow patched, or `old` untouched
 * @sideEffects none
 * @idempotent yes — repeated calls produce identical output
 */
export function patchWorkflowList(
  old: Workflow[] | undefined,
  ev: BroadcastEvent,
): Workflow[] | undefined {
  if (!old || old.length === 0) return old;
  let changed = false;
  const next = old.map((w) => {
    if (w.id !== ev.workflowId) return w;
    const patch: Partial<Workflow> = {};
    const d = (ev.details ?? {}) as Record<string, unknown>;

    // status: derive from event type when the payload doesn't carry one explicitly.
    if (typeof d.status === 'string') {
      patch.status = d.status as Workflow['status'];
    } else {
      switch (ev.eventType) {
        case 'workflow_started':
        case 'step_started':
        case 'gate_resubmitted':
        case 'gate_skipped':
          // A skipped gate releases the workflow back to the engine.
          patch.status = 'running';
          break;
        case 'gate_opened':
          patch.status = 'paused';
          break;
        case 'workflow_completed':
          patch.status = 'completed';
          break;
        case 'workflow_failed':
        case 'step_failed':
          patch.status = 'failed';
          break;
        case 'workflow_cancelled':
          patch.status = 'cancelled';
          break;
        // step_completed / gate_approved / step_scheduled / step_waiting_event:
        // status is not unambiguously derivable — leave existing value.
        default:
          break;
      }
    }
    if (typeof ev.stepIndex === 'number') {
      patch.currentStepIndex = ev.stepIndex;
    }
    if (typeof d.completedAt === 'string') {
      patch.completedAt = d.completedAt;
    } else if (
      ev.eventType === 'workflow_completed' ||
      ev.eventType === 'workflow_failed' ||
      ev.eventType === 'workflow_cancelled'
    ) {
      patch.completedAt = ev.timestamp;
    }
    if (typeof d.failureReason === 'string') {
      patch.failureReason = d.failureReason;
    }

    if (Object.keys(patch).length === 0) return w;
    changed = true;
    return { ...w, ...patch };
  });
  return changed ? next : old;
}

/**
 * Reconciles the React Query cache against an incoming WS event.
 * @param qc the global QueryClient
 * @param ev the typed BroadcastEvent
 * @returns void
 * @sideEffects calls qc.setQueryData (in-place patch) and qc.invalidateQueries (mark-stale)
 * @idempotent yes — repeat calls produce identical cache state
 */
export function handleBroadcast(qc: QueryClient, ev: BroadcastEvent): void {
  // (3) Always invalidate the open detail page (no-op if not mounted).
  invalidateFresh(qc, { queryKey: qk.workflow.root(ev.workflowId) });

  switch (ev.eventType) {
    case 'workflow_started':
    case 'workflow_completed':
    case 'workflow_failed':
    case 'workflow_cancelled':
    case 'step_started':
    case 'step_completed':
    case 'step_skipped':
    case 'step_failed':
    case 'step_scheduled':
    case 'step_waiting_event':
    case 'gate_opened':
    case 'gate_approved':
    case 'gate_rejected':
    case 'gate_resubmitted':
    case 'gate_skipped':
      // (1) In-place patch FIRST — avoids fetch flicker on workflow cards.
      //     setQueriesData on the broad prefix patches every list-filter
      //     variant (and the bare key) in place.
      qc.setQueriesData<Workflow[]>({ queryKey: qk.workflows.root() }, (old) =>
        patchWorkflowList(old, ev),
      );
      // (2) Narrow safety-net invalidate — only the actively-mounted list
      //     refetches; cached copies elsewhere keep the optimistic patch.
      invalidateFresh(qc, { queryKey: qk.workflows.root(), refetchType: 'active' });
      // Gate events also touch the pending-gates list + stats badges.
      if (
        ev.eventType === 'gate_opened' ||
        ev.eventType === 'gate_approved' ||
        ev.eventType === 'gate_rejected' ||
        ev.eventType === 'gate_resubmitted' ||
        ev.eventType === 'gate_skipped'
      ) {
        // Broad ['gates','pending'] prefix — left-prefix-matches every
        // event-scoped ['gates','pending',eventId] variant so the mounted
        // (active-event) queue + sidebar badge refresh live regardless of which
        // event they're scoped to.
        invalidateFresh(qc, { queryKey: qk.gates.root() });
      }
      // Stats banner reflects every workflow/step/gate transition.
      invalidateFresh(qc, { queryKey: qk.stats.root() });
      // Speaker roster is workflow-derived (GET /api/speakers reads the
      // speaker_onboarding workflows + their step states), so any workflow/
      // step/gate transition can move a speaker's row — refresh it live. This
      // is what makes the roster update the instant Sofia's gate is approved.
      invalidateFresh(qc, { queryKey: qk.speakers.root() });
      break;
    case 'email_confirmed':
      // (4) Invalidate the broad tracked-emails prefix so the Confirmations
      //     list reconciles whatever status filter it's showing (F12).
      invalidateFresh(qc, { queryKey: qk.trackedEmails.root() });
      invalidateFresh(qc, { queryKey: qk.stats.root() });
      break;
    case 'email_auto_confirmed':
      // Phase 6.3 D-WEBHOOK / D-POLICY-03: counterpart of email_confirmed
      // for the LLM-driven auto-advance path (confident ack). Same UI
      // affordance — the row transitions + stats badges update.
      invalidateFresh(qc, { queryKey: qk.trackedEmails.root() });
      invalidateFresh(qc, { queryKey: qk.stats.root() });
      break;
    case 'card_generation':
      // Speaker-card illustration generation started/finished (auto headshot
      // kick-off or system regenerate). The journey shows the "Generating
      // illustrations…" / "ready" states; an open card gate's candidates may
      // have been refreshed under the reviewer.
      invalidateFresh(qc, { queryKey: qk.speakers.root() });
      invalidateFresh(qc, { queryKey: qk.gates.root() });
      break;
    case 'email_in_conversation':
      // Phase 6.3 D-POLICY-03: PENDING|IN_CONVERSATION → IN_CONVERSATION
      // transition. Confirmations page renders the row with the "Needs
      // Manual Review" badge; Workflow Detail re-fetches to show the
      // appended classificationHistory entry.
      invalidateFresh(qc, { queryKey: qk.trackedEmails.root() });
      invalidateFresh(qc, { queryKey: qk.workflow.root(ev.workflowId) });
      break;
    case 'email_classified':
      // Phase 6.3 D-WEBHOOK / D-POLICY-04: forensic event — fires on every
      // classifier call regardless of status outcome. The detail-page
      // re-fetch at the top of this function already covers the workflow
      // refresh; the tracked-emails Pending list is intentionally NOT
      // invalidated because the row's status hasn't changed.
      break;
    case 'step_activity': {
      // Tier-2 wow visuals: append to a per-workflow activity ring buffer so
      // StepCard can render a live "found this" feed. Does NOT invalidate the
      // workflow detail query — those refetches are too coarse for the per-
      // event cadence. AuditEntry persistence (server-side) handles reload.
      const key = qk.activity(ev.workflowId);
      qc.setQueryData<ActivityRecord[]>([...key], (old) => {
        const next: ActivityRecord = {
          id: ev.eventId,
          workflowId: ev.workflowId,
          stepIndex: typeof ev.stepIndex === 'number' ? ev.stepIndex : null,
          stepId: typeof ev.details?.stepId === 'string' ? (ev.details.stepId as string) : null,
          label: typeof ev.details?.label === 'string' ? (ev.details.label as string) : '',
          kind: typeof ev.details?.kind === 'string' ? (ev.details.kind as string) : 'info',
          source: typeof ev.details?.source === 'string' ? (ev.details.source as string) : null,
          data:
            typeof ev.details?.data === 'object' && ev.details.data !== null
              ? (ev.details.data as Record<string, unknown>)
              : null,
          timestamp: ev.timestamp,
        };
        const list = old ?? [];
        // Dedup on id (in case of WS reconnect re-delivery).
        if (list.some((a) => a.id === next.id)) return list;
        // Cap at 500 entries per workflow so a long-running run doesn't grow unbounded.
        const merged = [...list, next];
        return merged.length > 500 ? merged.slice(-500) : merged;
      });
      break;
    }
    default: {
      // Exhaustiveness guard: every BroadcastEventType above is handled, so
      // here `ev.eventType` narrows to `never`. If a new event type is added
      // to broadcast.py + types.ts without a case here, this assignment fails
      // the build. At runtime an unrecognized event is a harmless no-op.
      const _exhaustive: never = ev.eventType;
      void _exhaustive;
      break;
    }
  }
}

/**
 * In-memory activity record shape the dashboard uses. Mirrors the
 * `step_activity` BroadcastEvent flattened for consumer ergonomics.
 */
export interface ActivityRecord {
  id: string;
  workflowId: string;
  stepIndex: number | null;
  stepId: string | null;
  label: string;
  kind: string;
  source: string | null;
  data: Record<string, unknown> | null;
  timestamp: string;
}
